/**
 * PlaywireTagged Component
 *
 * A React component that handles the display and lifecycle management of Playwire ad units.
 * This component implements lazy loading using intersection observer to only initialize
 * ads when they come into view, improving page performance and user experience.
 *
 * Key features:
 * - Lazy loading: Ads are only loaded when they enter the viewport
 * - Automatic cleanup: Properly destroys ad units when component unmounts
 * - Duplicate prevention: Prevents the same ad from being loaded multiple times
 * - Type-specific styling: Applies different CSS classes based on ad type
 */

import styles from './PlaywireTagged.module.css'
import { useInView } from 'react-intersection-observer'
import clsx from 'clsx'
import { BasicStyledComponent } from 'shared-definitions/types'
import { PlaywireContext } from 'shared-components/contexts/PlaywireContext'
import { useCallback, useEffect, useRef } from 'react'
import { logNotice, logWarning } from 'shared-code/log'
import { uniqueIdString } from 'shared-code/util'

/**
 * Props interface for the PlaywireTagged component
 * @extends BasicStyledComponent - Provides standard className prop for styling
 */
interface PlaywireTaggedProps extends BasicStyledComponent {
  /** The type of Playwire ad unit to display (e.g., 'med_rect_atf', 'leaderboard_btf') */
  type: PlaywireTaggedType
}

/**
 * PlaywireTagged functional component that renders and manages Playwire ad units
 *
 * @param className - Optional CSS class name for custom styling
 * @param type - The specific type of Playwire ad unit to display
 * @returns JSX element representing the ad container or null if ads are disabled
 */
const PlaywireTagged: React.VFC<PlaywireTaggedProps> = ({ className, type }) => {
  // Intersection observer hook - triggers when the element enters the viewport
  // triggerOnce: true ensures the ad is only loaded once, not on subsequent intersections
  const { ref, inView } = useInView({ triggerOnce: true })

  // Direct reference to the div element for manipulation and cleanup
  const divRef = useRef<HTMLDivElement>()

  // Access Playwire context containing the ad instance, enablement state, and CSS classes
  const { inst, enabled, adClassList } = PlaywireContext.useContainer()

  /**
   * Memoized function to set both refs (intersection observer and direct div ref)
   * Using useCallback prevents function recreation on each render, avoiding potential infinite loops
   */
  const setRefs = useCallback(
    (node: HTMLDivElement) => {
      divRef.current = node
      ref(node)
    },
    [ref]
  )

  /**
   * Main effect hook that handles ad initialization and cleanup
   * Dependencies: [inst, inView, type, enabled]
   *
   * This effect only runs when:
   * - Playwire instance is available (inst)
   * - Element is in viewport (inView)
   * - Ads are enabled (enabled)
   */
  useEffect(() => {
    // Early return if prerequisites aren't met
    if (!inst || !inView || !enabled) {
      return
    }

    // Destructure Playwire API methods from the instance
    const { addUnits, displayUnits, destroyUnits } = inst

    /**
     * Async function that initializes the ad unit
     * Returns a cleanup function that will be called when the component unmounts
     */
    const cleanup = (async () => {
      const { current } = divRef
      if (!current) {
        return
      }

      /**
       * Duplicate prevention: Check if the container already has child elements
       * This prevents the same ad from being initialized multiple times
       */
      if (current.childElementCount > 0) {
        // Return empty cleanup function if ad already exists
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        return
      }

      // Assign a unique ID to the container for Playwire to target
      current.id = uniqueIdString()

      try {
        // Log the ad initialization for debugging purposes
        logNotice('Adding new units to tyche', type)

        /**
         * Step 1: Add the ad unit configuration to Playwire
         * This tells Playwire what type of ad to load and where to place it
         */
        await addUnits([
          {
            type,
            selectorId: current.id,
          },
        ])

        /**
         * Step 2: Display all pending ad units
         * This actually renders the ads in their designated containers
         */
        await displayUnits()
      } catch (e) {
        // Log any errors during ad initialization
        logWarning('Playwire error', e)
      }

      /**
       * Return cleanup function that will be called on component unmount
       * This function finds the Playwire-generated element and destroys the ad unit
       */
      return () => {
        // Find the Playwire-generated tag within our container
        const inner = current.querySelector('.pw-tag')
        if (inner) {
          const { id } = inner
          if (id) {
            logNotice('Destroying tagged units', id)
            // Destroy the specific ad unit to free up resources
            void destroyUnits(id)
          }
        }
      }
    })()

    /**
     * Effect cleanup function - called when component unmounts or dependencies change
     * This ensures proper cleanup of ad units and DOM elements
     */
    return () => {
      logNotice('PlaywireTagged units cleanup', {
        i: inst === null,
        type,
        inView,
        enabled,
        inst,
      })

      // Execute the cleanup function returned by the initializer
      // eslint-disable-next-line promise/prefer-await-to-callbacks
      void cleanup.then(cb => cb && cb())

      // Clear the container content as an additional cleanup step
      const { current } = divRef
      if (current) {
        current.textContent = ''
      }
    }
  }, [inst, inView, type, enabled])

  // Early return: Don't render anything if ads are disabled
  if (!enabled) {
    return null
  }

  /**
   * Render the ad container div with appropriate styling and attributes
   *
   * Key attributes:
   * - data-ad-type: Identifies the ad type for debugging and analytics
   * - ref: Sets both intersection observer and direct references
   * - className: Combines default styles, ad-specific styles, and custom classes
   */
  return (
    <div
      data-ad-type={type}
      ref={setRefs}
      className={clsx(
        ...adClassList, // Global ad classes from Playwire context
        styles.container, // Base container styles
        className, // Custom className prop
        {
          // Conditional styles based on ad type
          [styles.rect]: type === 'med_rect_atf' || type === 'med_rect_btf',
          [styles.leaderboard]: type === 'leaderboard_atf' || type === 'leaderboard_btf',
        }
      )}
    />
  )
}

// Export the component as default for easy importing
export default PlaywireTagged
